import React, {useCallback, useState} from 'react';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatInput, useChatInputControls} from 'llm/chat-library/ChatInput';
import ChatScrollContainer from 'llm/chat-library/ChatScrollContainer';
import {mapAgentModeToAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {useActivityModalControls} from 'llm/deep-research/ActivityModal';
import {DeepResearchMessageThread} from 'llm/deep-research/DeepResearchMessageThread';
import notifications from 'notifications';

export interface DeepResearchChatProps {
  chatId: string;
}

export function DeepResearchChat({chatId}: DeepResearchChatProps): JSX.Element {
  const {getFileUploads, ...controls} = useChatInputControls(ChatType.DEEP_RESEARCH);
  const {inputText, setInputText, agentMode, publicDataEnabled, setSelectedFiles} = controls;
  const {events, progressData, isAssistantRunning, sendMessage, isLoading} = useMessageThread({
    chatSessionId: chatId,
    includeToolCalls: true,
  });
  const {openActivityModal} = useActivityModalControls();
  const [latestUserMessageInfo, setLatestUserMessageInfo] = useState<LatestUserMessageInfo | null>(null);

  const handleSubmitMessage = useCallback(async () => {
    if (!agentMode) {
      // This is unexpected, but just in case, notify the user why their message was not sent.
      notifications.error('An agent type must be selected before sending a message', {
        id: 'dr-agent-mode-required',
      });
      return;
    }
    const message = inputText;
    setInputText('');
    setSelectedFiles([]);
    await sendMessage({
      text: message,
      fileUploads: getFileUploads(),
      agentType: mapAgentModeToAgentType(agentMode),
      publicDataEnabled,
    });
  }, [inputText, publicDataEnabled, sendMessage, getFileUploads, setInputText, setSelectedFiles, agentMode]);

  return (
    <ChatScrollContainer
      chatMessagesElement={
        <DeepResearchMessageThread
          chatSessionId={chatId}
          isLoading={isLoading}
          messages={events}
          progressData={progressData}
          openActivityModal={openActivityModal}
          onUpdateLatestUserMessageInfo={setLatestUserMessageInfo}
        />
      }
      chatInputElement={
        <ChatInput
          chatSessionId={chatId}
          {...controls}
          onSubmit={handleSubmitMessage}
          inputDisabled={isAssistantRunning}
        />
      }
      latestUserMessageInfo={latestUserMessageInfo}
      autoScroll={true}
    />
  );
}
